import { api, ApiError } from '../api';

// 500 MB — the architect's spec hard cap (`524_288_000`). The operator brief
// mentions "5 GB cap" but the architect intentionally tightened this in the
// fresh v3 spec; the spec wins per the brief's own precedence rule.
const MAX_UPLOAD_BYTES = 524_288_000;

export interface UploadResult {
  uploadId: string;
  filename: string;
  actualSizeBytes: number;
}

export interface UploaderCallbacks {
  user: { displayName: string };
  onUploaded: (r: UploadResult) => void;
  onError: (err: ApiError | Error) => void;
  onSignOut: () => void;
  onDeleteAccount: () => void;
}

export function renderUploader(host: HTMLElement, cb: UploaderCallbacks): void {
  const card = document.createElement('section');
  card.className = 'card';

  const hello = document.createElement('p');
  hello.className = 'small muted';
  hello.textContent = `Signed in as ${cb.user.displayName}.`;

  const signOut = document.createElement('button');
  signOut.type = 'button';
  signOut.className = 'btn btn-link';
  signOut.textContent = 'Sign out';
  signOut.addEventListener('click', cb.onSignOut);

  const deleteAccount = document.createElement('button');
  deleteAccount.type = 'button';
  deleteAccount.className = 'btn btn-link btn-danger';
  deleteAccount.textContent = 'Delete my account';
  deleteAccount.addEventListener('click', () => {
    const ok = window.confirm(
      'Delete your account?\n\nThis permanently removes your sign-in, all your import history, and any uploaded files still on our servers. The notes already in OneNote stay where they are.\n\nThis can’t be undone.',
    );
    if (ok) cb.onDeleteAccount();
  });

  const h2 = document.createElement('h2');
  h2.textContent = 'Upload your Evernote export';

  const instructions = document.createElement('p');
  instructions.innerHTML =
    'In Evernote, choose <strong>File → Export Notes → ENEX (.enex)</strong>. Files up to 500 MB.';

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.enex,application/xml';
  input.id = 'enex-file';

  const label = document.createElement('label');
  label.setAttribute('for', 'enex-file');
  label.className = 'file-label';
  label.textContent = 'Choose .enex file';

  const sizeWarn = document.createElement('p');
  sizeWarn.className = 'small muted';
  sizeWarn.setAttribute('aria-live', 'polite');

  const progress = document.createElement('div');
  progress.className = 'progress hidden';
  progress.setAttribute('role', 'progressbar');
  progress.setAttribute('aria-valuemin', '0');
  progress.setAttribute('aria-valuemax', '100');
  const progressBar = document.createElement('div');
  progressBar.className = 'progress-bar';
  progressBar.style.width = '0%';
  progress.append(progressBar);

  const progressLabel = document.createElement('p');
  progressLabel.className = 'small muted hidden';

  const startBtn = document.createElement('button');
  startBtn.type = 'button';
  startBtn.className = 'btn btn-primary';
  startBtn.textContent = 'Upload';
  startBtn.disabled = true;

  let currentFile: File | null = null;
  let abortCtl: AbortController | null = null;

  input.addEventListener('change', () => {
    currentFile = input.files?.[0] ?? null;
    sizeWarn.textContent = '';
    progress.classList.add('hidden');
    progressLabel.classList.add('hidden');
    startBtn.disabled = true;
    if (!currentFile) return;

    if (!/\.enex$/i.test(currentFile.name)) {
      sizeWarn.textContent = "That doesn't look like an Evernote export. Look for a file ending in .enex.";
      return;
    }
    if (currentFile.size <= 0) {
      sizeWarn.textContent = 'That file looks empty. Re-export from Evernote.';
      return;
    }
    if (currentFile.size > MAX_UPLOAD_BYTES) {
      sizeWarn.textContent = `That file is ${humanBytes(currentFile.size)} — over the 500 MB web limit. Split your export in Evernote.`;
      return;
    }
    sizeWarn.textContent = `Ready to upload ${currentFile.name} (${humanBytes(currentFile.size)}).`;
    startBtn.disabled = false;
  });

  startBtn.addEventListener('click', async () => {
    if (!currentFile) return;
    startBtn.disabled = true;
    input.disabled = true;

    progress.classList.remove('hidden');
    progressLabel.classList.remove('hidden');
    progressLabel.textContent = 'Requesting upload URL…';

    try {
      const { uploadId, putUrl } = await api.createUpload({
        filename: currentFile.name,
        sizeBytes: currentFile.size,
      });

      progressLabel.textContent = 'Uploading…';
      abortCtl = new AbortController();

      await api.putToSignedUrl(putUrl, currentFile, {
        signal: abortCtl.signal,
        onProgress: (p) => {
          progressBar.style.width = `${p.pct}%`;
          progress.setAttribute('aria-valuenow', String(p.pct));
          progressLabel.textContent = `Uploading… ${p.pct}%`;
        },
      });

      progressLabel.textContent = 'Finishing…';
      const done = await api.completeUpload(uploadId);

      progressBar.style.width = '100%';
      progress.setAttribute('aria-valuenow', '100');
      progressLabel.textContent = 'Upload complete.';

      cb.onUploaded({ uploadId, filename: currentFile.name, actualSizeBytes: done.actualSizeBytes });
    } catch (err) {
      input.disabled = false;
      startBtn.disabled = false;
      cb.onError(err as Error);
    } finally {
      abortCtl = null;
    }
  });

  card.append(hello, signOut, deleteAccount, h2, instructions, label, input, sizeWarn, startBtn, progress, progressLabel);
  host.replaceChildren(card);
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
